import { BreakpointObserver } from '@angular/cdk/layout';
import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, Input, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { TRANSLOCO_SCOPE, TranslocoModule, TranslocoService } from '@jsverse/transloco';
import * as Highcharts from 'highcharts';
import { ICertificationAssignmentStatsResponse } from 'src/app/interfaces/reportingV2/icertification-assignment-stats-response';
import { TranslationService } from 'src/app/services/translation.service';

@Component({
  selector: 'app-certification-chart',
  templateUrl: './certification-chart.component.html',
  styleUrls: ['./certification-chart.component.scss'],
  standalone: true,
  providers: [
    {
      provide: TRANSLOCO_SCOPE,
      useValue: 'reportingV2',
    }
  ],
  imports: [CommonModule,
    TranslocoModule],
})
export class CertificationChartComponent implements OnInit {
  @ViewChild('chartContainer', { static: false }) chartContainer!: ElementRef;
  @Input() certificationStats: ICertificationAssignmentStatsResponse[] = [];
  chart: Highcharts.Chart | undefined;
  chartOptions: Highcharts.Options = {};
  isTranslationLoaded: boolean = false;
  isSmallScreen: boolean = false;

  constructor(
    private translocoService: TranslocoService,
    private translationService: TranslationService,
    private cdr: ChangeDetectorRef,
    private breakpointObserver: BreakpointObserver,
  ) { }

  ngOnInit() {
    this.translationService.loadScopedTranslation('reportingV2').then(() => {
      this.isTranslationLoaded = true;
      this.breakpointObserver.observe(['(max-width: 768px)'])
      .subscribe(result => {
        this.isSmallScreen = result.matches;
        this.initChart();
        this.cdr.detectChanges();
        this.drawChart();
      });
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['certificationStats'] && this.isTranslationLoaded) {
      console.log('ngOnChanges for practice chart********************');
      this.initChart();
      this.cdr.detectChanges(); 
      this.drawChart();
    }
  }

  private initChart() {
    const assignedLabel = this.translocoService.translate('ASSIGNED_LABEL', {}, 'reportingV2');
    const inProgressLabel = this.translocoService.translate('IN_PROGRESS_LABEL', {}, 'reportingV2');
    const certifiedLabel = this.translocoService.translate('CERTIFIED_LABEL', {}, 'reportingV2');
    const outOfAttemptsLabel = this.translocoService.translate('OUT_OF_ATTEMPTS_LABEL', {}, 'reportingV2');
    const assignments = this.certificationStats;
    this.chartOptions = {
      chart: {
        type: 'column',
        height: 400,
        ...(this.isSmallScreen && {
          scrollablePlotArea: {
            minWidth: 1500,
            scrollPositionX: 0
          }
        })
      },

      title: {
        text: '',
      },
      xAxis: {
        categories: assignments.map(a => a.moduleAssignmentName),
        offset: 20,
        labels: {
          style: {
            fontSize: '12px',
            color: '#475467',
            fontWeight: '500',

          },
          formatter: function() {
            const maxLength = 20;
            const label = String(this.value); 
            
            if (label.length > maxLength) {
              const truncated = label.substring(0, maxLength) + '...';
              return `<span title="${label}">${truncated}</span>`;
            }
            return label;
          }
        },
        lineWidth: 0,
      },
      yAxis: {
        title: {
          text: this.translocoService.translate('ACTIVE_USERS', {}, 'reportingV2'),
          style: {
            fontSize: '12px',
            fontWeight: '500',
            color: '475467',
          }
        },
        margin: 20,
        min: 0,
        allowDecimals: false,
      },
      legend: {
        enabled: false,
      },
      tooltip: {
        useHTML: true,
        backgroundColor: 'transparent',
        borderWidth: 0,
        shadow: false,
        stickOnContact: true,
        outside: true,
        formatter: function () {
          const pointIndex = this.point.index;
          const assignmentName = this.x;

          const assignmentData = assignments.find((a: any) => a.moduleAssignmentName === assignmentName);

          if (assignmentData) {
            return `
          <div class="custom-tooltip">
            <div class="tooltip-content">
              <div class="header">${assignmentData.moduleAssignmentName}</div>
              <div class="status-container">
              ${assignmentData.assignedUsersCount} ${assignedLabel}<br/>
              ${assignmentData.inProgressUsersCount} ${inProgressLabel}<br/>
              ${assignmentData.certifiedUsersCount} ${certifiedLabel}<br/>
              ${assignmentData.outOfAttemptsUsersCount} ${outOfAttemptsLabel}<br/>
              </div>
              
            </div>
            <div class="tooltip-arrow"></div>
          </div>
        `;
          }

          return `<b>${assignmentName}</b>`;
        }
      },
      plotOptions: {
        series: {
          states: {
            hover: {
              enabled: false,
              halo: {
                size: 0
              }
            },
            inactive: {
              enabled: false
            }
          }
        },
        column: {
          stacking: 'normal',
          borderWidth: 0,
          borderRadius: 8,
        }
      },
      series: [
        {
          name: 'Assigned',
          type: 'column',
          data: assignments.map(a => a.assignedUsersCount),
          color: '#EAECF0'
        },
        {
          name: 'InProgress',
          type: 'column',
          data: assignments.map(a => a.inProgressUsersCount),
          color: '#52A4B0',
        },
        {
          name: 'Certified',
          type: 'column',
          data: assignments.map(a => a.certifiedUsersCount),
          color: '#6EDEA1'
        },
        {
          name: 'OutOfAttempts',
          type: 'column',
          data: assignments.map(a => a.outOfAttemptsUsersCount),
          color: '#FDA29B'
        }
      ],
      credits: {
        enabled: false
      },
      accessibility: {
        enabled: false
      },
    };
  }

  private drawChart() {
    const scrollPosition = window.scrollY;
    if (this.chartContainer) {
      this.chart = Highcharts.chart(this.chartContainer.nativeElement, this.chartOptions);
    }
    window.scrollTo(0, scrollPosition);
  }
}
